import React, { useEffect } from "react";
import { hostPixelAlreadyReady, resolveFbPixelId } from "./fb-pixel-id";

type FacebookPixelProps = {
  /** From host widget config (`fbPixelId`), preferred over shared env when set. */
  fbPixelId?: string;
};

function ensureFbqStub(w: any, document: Document) {
  if (typeof w.fbq === "function") {
    return;
  }

  const fbq: any = function (...args: any[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  };
  w.fbq = fbq;
  if (!w._fbq) {
    w._fbq = fbq;
  }
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Browser Pixel bootstrap.
 * - If the host page already called fbq('init') (Basketmouth), skip second init + PageView.
 * - Else init resolved Pixel ID (domain / config / REACT_APP_FB_PIXEL_ID).
 * Never embed CAPI tokens here.
 */
function FacebookPixel({ fbPixelId }: FacebookPixelProps) {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const pixelId = resolveFbPixelId(fbPixelId);
    if (!pixelId) {
      return;
    }

    const w = window as any;

    if (hostPixelAlreadyReady()) {
      // Host already inited (and usually fired PageView). Ensure our ID is also registered
      // if host used the same Basketmouth Pixel — init is idempotent per Pixel ID.
      try {
        w.fbq("init", pixelId);
      } catch {
        /* ignore */
      }
      return;
    }

    ensureFbqStub(w, document);
    w.fbq("init", pixelId);
    w.fbq("track", "PageView");
  }, [fbPixelId]);

  return null;
}

export default FacebookPixel;
